// src/components/CierreMes.jsx
import { useMemo, useState, useEffect } from "react";
import { descargarFacturasZip } from "../api";

const normCode = (s) => {
  const value = String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[_\s]/g, "-")
    .trim();

  if (value === "psicologa-g") return "psicologia-g";
  if (value === "psicologa-m") return "psicologia-m";
  if (value === "psicologia-adultos") return "psicologia-adultos";
  if (value === "psicologia") return "psicologia";

  return value;
};

const getSpecialtyCode = (p) =>
  normCode(
    p.specialty_code ||
      p.specialty ||
      p.specialty_name ||
      ""
  );

// Ya no mapeamos G/M a otras claves
const mapEspecialidadAFiltro = (codeRaw) => normCode(codeRaw);

export default function CierreMes({
  mes,
  setMes,
  pagos,
  resumen, // compatibilidad
  filtroEspecialidades,
  setFiltroEspecialidades,
  specialtiesOptions,
  loading,
  error,
}) {
  const [verTarjeta, setVerTarjeta] = useState(true);
  const [verEfectivo, setVerEfectivo] = useState(true);

  const pagosFiltrados = useMemo(() => {
    return (pagos || []).filter((p) => {
      const code = mapEspecialidadAFiltro(
        p.specialty_code || p.specialty || p.specialty_name || "",
      );
      const method = String(p.payment_method || "").toLowerCase().trim();

      let pasaEspecialidad = true;
      if (code) {
        const valorFiltro = filtroEspecialidades?.[code];
        if (valorFiltro !== undefined) {
          pasaEspecialidad = !!valorFiltro;
        }
      }

      const pasaMetodo =
        (verTarjeta && method === "tarjeta") ||
        (verEfectivo && method === "efectivo") ||
        (!method && verTarjeta);

      return pasaEspecialidad && pasaMetodo;
    });
  }, [pagos, filtroEspecialidades, verTarjeta, verEfectivo]);

  const [dlTarjeta, setDlTarjeta] = useState(true);
  const [dlEfectivo, setDlEfectivo] = useState(true);
  const [dlEspecialidades, setDlEspecialidades] = useState({});

  useEffect(() => {
    setDlEspecialidades((prev) => {
      const next = { ...prev };

      (specialtiesOptions || []).forEach((s) => {
        const k = normCode(s.code);
        if (next[k] === undefined) next[k] = true;
      });

      return next;
    });
  }, [specialtiesOptions]);

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [downloadInfo, setDownloadInfo] = useState("");

  const pagosParaDescarga = useMemo(() => {
    return (pagos || []).filter((p) => {
      const code = mapEspecialidadAFiltro(
        p.specialty_code || p.specialty || p.specialty_name || "",
      );
      const method = String(p.payment_method || "").toLowerCase().trim();

      if (code && dlEspecialidades[code] === false) return false;
      if (method === "tarjeta" && !dlTarjeta) return false;
      if (method === "efectivo" && !dlEfectivo) return false;

      return true;
    });
  }, [pagos, dlEspecialidades, dlTarjeta, dlEfectivo]);

  const onDescargarZip = async () => {
    try {
      setDownloading(true);
      setDownloadError("");
      setDownloadInfo("");

      if (!mes) throw new Error("No hay mes seleccionado");

      const ids = (pagosParaDescarga || [])
        .map((p) => Number(p.id))
        .filter((x) => Number.isFinite(x) && x > 0);

      if (!ids.length) throw new Error("No hay pagos para descargar");

      await descargarFacturasZip({ month: mes, paymentIds: ids });

      setDownloadInfo(`ZIP generado con ${ids.length} pagos.`);
    } catch (e) {
      setDownloadError(e?.message || "Error generando ZIP");
    } finally {
      setDownloading(false);
    }
  };

  const calcularResumen = (items = []) => {
    let total = 0;
    let efectivo = 0;
    let tarjeta = 0;

    items.forEach((p) => {
      const amt = Number(p.amount) || 0;
      total += amt;

      const method = String(p.payment_method || "").toLowerCase().trim();
      if (method === "efectivo") efectivo += amt;
      else if (method === "tarjeta") tarjeta += amt;
    });

    return { total, efectivo, tarjeta, count: items.length };
  };

  const resumenFiltrado = calcularResumen(pagosFiltrados);

const calcularFactorCentro = (codeRaw) => {
  const code = normCode(codeRaw);

  if (code === "logopeda" || code === "nutricion") return 0.3;

  if (code === "psicologia-g" || code === "psicologia-m") {
    return 0.4;
  }

  if (code === "psicologia") {
    return 1; // Teresa / centro: 100%
  }

  return 1;
};

  const resumenDetallado = useMemo(() => {
    let totalTarjeta = 0;
    let totalEfectivo = 0;

    let centroTarjeta = 0;
    let centroEfectivo = 0;

    let profesionalTarjeta = 0;
    let profesionalEfectivo = 0;

    let logopediaCentro = 0;
    let nutricionCentro = 0;
    let psicologiaGCentro = 0;
    let psicologiaMCentro = 0;
    let psicologiaCentro = 0;
    let psicologiaAdultosCentro = 0;

    (pagosFiltrados || []).forEach((p) => {
      const amt = Number(p.amount) || 0;
      const method = String(p.payment_method || "").toLowerCase().trim();
      const codeReal = getSpecialtyCode(p);

      const factorCentro = calcularFactorCentro(codeReal);
      const parteCentro = amt * factorCentro;
      const parteProfesional = amt - parteCentro;

      if (method === "efectivo") {
        totalEfectivo += amt;
        centroEfectivo += parteCentro;
        profesionalEfectivo += parteProfesional;
      } else {
        totalTarjeta += amt;
        centroTarjeta += parteCentro;
        profesionalTarjeta += parteProfesional;
      }

      if (codeReal === "logopeda") logopediaCentro += parteCentro;
      if (codeReal === "nutricion") nutricionCentro += parteCentro;
      if (codeReal === "psicologia-g") psicologiaGCentro += parteCentro;
      if (codeReal === "psicologia-m") psicologiaMCentro += parteCentro;
      if (codeReal === "psicologia") psicologiaCentro += parteCentro;
      if (codeReal === "psicologia-adultos") {
        psicologiaAdultosCentro += parteCentro;
      }
    });

    return {
      totalTarjeta,
      totalEfectivo,
      totalGlobal: totalTarjeta + totalEfectivo,

      centroTarjeta,
      centroEfectivo,
      centroGlobal: centroTarjeta + centroEfectivo,

      profesionalTarjeta,
      profesionalEfectivo,
      profesionalGlobal: profesionalTarjeta + profesionalEfectivo,

      logopediaCentro,
      nutricionCentro,
      psicologiaGCentro,
      psicologiaMCentro,
      psicologiaCentro,
      psicologiaAdultosCentro,
    };
  }, [pagosFiltrados]);

  return (
    <div className="cierre-panel">
      <div className="pagos-actions">
        <div className="month-filter">
          <label>
            Mes:
            <input
              type="month"
              value={mes}
              onChange={(e) => setMes(e.target.value)}
            />
          </label>
        </div>

        <div className="especialidad-filter">
          {(specialtiesOptions || []).map((s) => {
            const code = normCode(s.code);

            return (
              <label key={s.code} className="chk-especialidad">
                <input
                  type="checkbox"
                  checked={filtroEspecialidades?.[code] !== false}
                  onChange={(e) =>
                    setFiltroEspecialidades((prev) => ({
                      ...prev,
                      [code]: e.target.checked,
                    }))
                  }
                />
                {s.label}
              </label>
            );
          })}
        </div>
      </div>

      <div
        className="cierre-panel"
        style={{
          marginTop: 12,
          padding: 12,
          border: "1px solid #ddd",
          borderRadius: 10,
        }}
      >
        <strong>Mostrar en tabla y resumen</strong>

        <div style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={verTarjeta}
              onChange={(e) => setVerTarjeta(e.target.checked)}
            />
            💳 Tarjeta
          </label>

          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={verEfectivo}
              onChange={(e) => setVerEfectivo(e.target.checked)}
            />
            💵 Efectivo
          </label>
        </div>
      </div>

      <div
        className="cierre-panel"
        style={{
          marginTop: 12,
          padding: 12,
          border: "1px solid #ddd",
          borderRadius: 10,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <strong>Descargar facturas del mes ({mes})</strong>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              Estos filtros solo afectan al ZIP, no a la tabla.
            </div>
          </div>

          <button
            type="button"
            className="btn"
            onClick={onDescargarZip}
            disabled={downloading || loading || pagosParaDescarga.length === 0}
          >
            {downloading ? "Generando ZIP..." : "Descargar ZIP"}
          </button>
        </div>

        <div style={{ marginTop: 10, display: "flex", gap: 16, flexWrap: "wrap" }}>
          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={dlTarjeta}
              onChange={(e) => setDlTarjeta(e.target.checked)}
            />
            💳 Tarjeta
          </label>

          <label className="chk-especialidad">
            <input
              type="checkbox"
              checked={dlEfectivo}
              onChange={(e) => setDlEfectivo(e.target.checked)}
            />
            💵 Efectivo
          </label>
        </div>

        <div className="especialidad-filter" style={{ marginTop: 10 }}>
          {(specialtiesOptions || []).map((s) => {
            const code = normCode(s.code);

            return (
              <label key={`dl-${s.code}`} className="chk-especialidad">
                <input
                  type="checkbox"
                  checked={dlEspecialidades[code] !== false}
                  onChange={(e) =>
                    setDlEspecialidades((prev) => ({
                      ...prev,
                      [code]: e.target.checked,
                    }))
                  }
                />
                {s.label}
              </label>
            );
          })}
        </div>

        <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>
          Facturas incluidas: <strong>{pagosParaDescarga.length}</strong>
        </div>

        {downloadInfo ? (
          <div style={{ marginTop: 8, fontSize: 12 }}>{downloadInfo}</div>
        ) : null}

        {downloadError ? (
          <div style={{ marginTop: 8, color: "red", whiteSpace: "pre-wrap" }}>
            {downloadError}
          </div>
        ) : null}
      </div>

      {loading ? (
        <p>Cargando cierre de mes...</p>
      ) : error ? (
        <p style={{ color: "red" }}>{error}</p>
      ) : (
        <>
          <div className="cierre-resumen">
            <div className="cierre-card">
              <strong>Total mes:</strong> {resumenDetallado.totalGlobal.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Efectivo:</strong> {resumenDetallado.totalEfectivo.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Tarjeta:</strong> {resumenDetallado.totalTarjeta.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Nº pagos:</strong> {resumenFiltrado.count}
            </div>

            <div className="cierre-card">
              <strong>Logopedia 30% (centro):</strong>{" "}
              {resumenDetallado.logopediaCentro.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Nutrición 30% (centro):</strong>{" "}
              {resumenDetallado.nutricionCentro.toFixed(2)} €
            </div>


            <div className="cierre-card">
              <strong>Psicología G 40% (centro):</strong>{" "}
              {resumenDetallado.psicologiaGCentro.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Psicología M 40% (centro):</strong>{" "}
              {resumenDetallado.psicologiaMCentro.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Profesional tarjeta:</strong>{" "}
              {resumenDetallado.profesionalTarjeta.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Profesional efectivo:</strong>{" "}
              {resumenDetallado.profesionalEfectivo.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Total profesional:</strong>{" "}
              {resumenDetallado.profesionalGlobal.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Centro tarjeta:</strong>{" "}
              {resumenDetallado.centroTarjeta.toFixed(2)} €
            </div>

            <div className="cierre-card">
              <strong>Centro efectivo:</strong>{" "}
              {resumenDetallado.centroEfectivo.toFixed(2)} €
            </div>

            <div className="cierre-card cierre-card-destacado">
              <strong>Total real centro:</strong>{" "}
              {resumenDetallado.centroGlobal.toFixed(2)} €
            </div>
          </div>

          {pagosFiltrados.length === 0 ? (
            <p>No hay pagos en este mes con los filtros seleccionados.</p>
          ) : (
            <table className="pv-table pagos">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Paciente</th>
                  <th>Especialidad</th>
                  <th>Servicio</th>
                  <th>Importe (€)</th>
                  <th>Método</th>
                </tr>
              </thead>
              <tbody>
                {pagosFiltrados.map((p) => (
                  <tr key={p.id}>
                    <td>{new Date(p.date).toLocaleDateString("es-ES")}</td>
                    <td
                      className="paciente-cell"
                      title={`${p.paciente} ${p.apellidos || ""}`}
                    >
                      <strong>{p.paciente}</strong> {p.apellidos}
                    </td>
                    <td>{p.specialty_name || p.specialty_code || p.specialty || "—"}</td>
                    <td>{p.service_name || "-"}</td>
                    <td style={{ textAlign: "center" }}>{p.amount} €</td>
                    <td>
                      {String(p.payment_method || "").toLowerCase() === "efectivo"
                        ? "💵 Efectivo"
                        : "💳 Tarjeta"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}